"use client";

/**
 * @file Global consent gate. Mounted from each route-group shell so the
 * consent UX runs across `(shop)`, `(account)`, and `(public-account)`.
 *
 * Resolution rules (mirrors `legal-copy-spec.md` §10.3):
 *
 *  1. account-current → silently refresh local, no toast.
 *  2. local-current but account stale/missing → silent PUT `guest_reconciliation`,
 *     no toast.
 *  3. both stale/missing → show toast; on acceptance, write local immediately
 *     and (if authenticated) PUT `continued_use`.
 *
 * The gate is intentionally a thin client wrapper that defers comparison
 * work to the pure helpers in `legal-consent-state.ts`.
 */
import { useEffect, useState } from "react";
import {
  LEGAL_CURRENT_VERSIONS,
  type LegalConsentDocumentId,
  type LegalConsentStatusDTO,
} from "@wmart/shared-types";

import { ApiError, clientApi } from "@/lib/api-client";
import {
  deriveStaleDocumentsForSnapshot,
  isSnapshotCurrent,
} from "./legal-consent-state";
import {
  readLocalLegalConsent,
  writeLocalLegalConsent,
  type LocalLegalConsentSnapshot,
} from "./legal-consent-storage";
import { LegalConsentToast } from "./LegalConsentToast";

interface LegalConsentGateProps {
  /**
   * Whether the visitor has an authenticated customer session. Layouts
   * derived this from their server-side `safeGetMe()` call. The gate uses
   * it to decide whether to consult the account-state endpoint and which
   * source to record on acceptance.
   */
  hasCustomerSession: boolean;
}

type GateState =
  | { kind: "idle" }
  | { kind: "show"; staleDocuments: LegalConsentDocumentId[] };

const SESSION_SUPPRESSION_KEY = "wmart.legal-consent.v1.session-suppressed";

function markSessionSuppressed() {
  try {
    sessionStorage?.setItem(SESSION_SUPPRESSION_KEY, "1");
  } catch {
    // ignore
  }
}

function isSessionSuppressed(): boolean {
  try {
    return sessionStorage?.getItem(SESSION_SUPPRESSION_KEY) === "1";
  } catch {
    return false;
  }
}

function snapshotFromAccount(
  status: LegalConsentStatusDTO,
): LocalLegalConsentSnapshot {
  return {
    acceptedVersions: { ...status.acceptedVersions },
    acceptedAt: status.acceptedAt,
    source: "authenticated",
  };
}

export function LegalConsentGate({ hasCustomerSession }: LegalConsentGateProps) {
  const [state, setState] = useState<GateState>({ kind: "idle" });

  useEffect(() => {
    if (isSessionSuppressed()) return;
    let cancelled = false;

    async function evaluate() {
      const localSnapshot = readLocalLegalConsent();
      const localCurrent = isSnapshotCurrent(localSnapshot.acceptedVersions);

      if (!hasCustomerSession) {
        if (localCurrent) return;
        if (cancelled) return;
        setState({
          kind: "show",
          staleDocuments: deriveStaleDocumentsForSnapshot(
            localSnapshot.acceptedVersions,
          ),
        });
        return;
      }

      // Authenticated: consult account state.
      let accountStatus: LegalConsentStatusDTO | null = null;
      try {
        accountStatus = await clientApi.get<LegalConsentStatusDTO>(
          "/v1/storefront/me/legal-consent",
        );
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          // Treat as anonymous fallback.
          if (!localCurrent && !cancelled) {
            setState({
              kind: "show",
              staleDocuments: deriveStaleDocumentsForSnapshot(
                localSnapshot.acceptedVersions,
              ),
            });
          }
          return;
        }
        // Network or server failure — do not pester the user.
        return;
      }
      if (cancelled || !accountStatus) return;

      if (accountStatus.isCurrent) {
        if (!localCurrent) {
          writeLocalLegalConsent(snapshotFromAccount(accountStatus));
        }
        return;
      }

      if (localCurrent) {
        // Account is stale/missing but local is current — silent backfill.
        try {
          const updated = await clientApi.put<LegalConsentStatusDTO>(
            "/v1/storefront/me/legal-consent",
            { source: "guest_reconciliation" },
          );
          if (cancelled) return;
          writeLocalLegalConsent({
            ...snapshotFromAccount(updated),
            source: "guest_reconciliation",
          });
        } catch {
          // ignore — local already current
        }
        return;
      }

      if (cancelled) return;
      setState({
        kind: "show",
        staleDocuments: accountStatus.staleDocuments,
      });
    }

    void evaluate();
    return () => {
      cancelled = true;
    };
  }, [hasCustomerSession]);

  if (state.kind !== "show") return null;

  return (
    <LegalConsentToast
      staleDocuments={state.staleDocuments}
      onAccept={() => {
        const acceptedAt = new Date().toISOString();
        writeLocalLegalConsent({
          acceptedVersions: { ...LEGAL_CURRENT_VERSIONS },
          acceptedAt,
          source: hasCustomerSession ? "authenticated" : "anonymous",
        });
        markSessionSuppressed();
        setState({ kind: "idle" });
        if (hasCustomerSession) {
          clientApi
            .put<LegalConsentStatusDTO>(
              "/v1/storefront/me/legal-consent",
              { source: "continued_use" },
            )
            .catch(() => {
              // best-effort
            });
        }
      }}
      onDismiss={() => {
        markSessionSuppressed();
        setState({ kind: "idle" });
      }}
    />
  );
}
